import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  FileCheck, 
  BarChart3, 
  Users, 
  Shield, 
  TrendingUp, 
  Clock,
  CheckCircle,
  ArrowRight,
  Truck,
  Zap,
  Package,
  DollarSign
} from 'lucide-react';
import { Link } from 'react-router-dom';

const ServicesPage = () => {
  const [activeService, setActiveService] = useState('invoice-auditing');

  const mainServices = [
    {
      id: 'invoice-auditing',
      icon: <FileCheck className="w-8 h-8 text-blue-600" />,
      title: "Audit and Capital",
      shortDesc: "Comprehensive auditing for all your business invoices",
      fullDesc: "Our expert team provides thorough invoice auditing services across freight, utilities, and general supplier invoices. We identify discrepancies, prevent overpayments, and ensure complete accuracy in your accounts payable process - which we can also optimize on your behalf.",
      features: [
        "Freight invoice verification and audit",
        "Utility bill analysis and validation",
        "Supplier invoice accuracy checks",
        "Automated discrepancy detection",
        "Detailed audit reports and recommendations",
        "PO and Accruals mapping"
      ],
      benefits: [
        "Reduce invoice processing errors by 95%",
        "Save 20-30% on operational costs",
        "Improve vendor relationship management",
        "Ensure regulatory compliance"
      ]
    },
    {
      id: 'data-intelligence',
      icon: <BarChart3 className="w-8 h-8 text-teal-600" />,
      title: "Data and BPO",
      shortDesc: "Advanced analytics and business intelligence solutions",
      fullDesc: "Leverage our BPO support Team to transform your raw data into actionable insights with our comprehensive data intelligence platform. We provide real-time analytics, predictive modeling, and custom reporting to drive informed business decisions.",
      features: [
        "Real-time dashboard and reporting",
        "Predictive analytics and forecasting",
        "Custom KPI tracking and monitoring",
        "Data visualization and presentation",
        "Automated report generation"
      ],
      benefits: [
        "Make data-driven decisions faster",
        "Identify cost-saving opportunities",
        "Improve operational efficiency",
        "Gain competitive market insights"
      ]
    },
    {
      id: 'escalation-handling',
      icon: <Users className="w-8 h-8 text-blue-600" />,
      title: "Insights and AI",
      shortDesc: "AI driven reporting for effective decision making",
      fullDesc: "Dig deeper into your datasets to uncover trends, mitigate risks, and optimize processes - turning complex financial reviews into clear, strategic advantages for your business.",
      features: [
        "Self-learning proprietary tools at your fingertip",
        "Enhanced 3rd party communication and negotiations",
        "Automated billing errors detection and escalation",
        "Real-time, full access to your own data",
        "Customizable SOPs"
      ],
      benefits: [
        "Maintain positive vendor relationships",
        "Reduce resolution time by 60%",
        "Professional representation",
        "Focus on core business activities"
      ]
    },
    {
      id: 'payable-services',
      icon: <Shield className="w-8 h-8 text-teal-600" />,
      title: "PBX Projects",
      shortDesc: "Put your untapped but valuable data to work!",
      fullDesc: "We can help you use non-core data streams (e.g., Customs data, 3rd party transactional data, etc..) to reveal untapped optimization and cost-saving opportunities, transforming unused assets into direct business growth drivers.",
      features: [
        "Project Management-style execution",
        "Raw Data processing, analysis and refinement",
        "Framework and SOP design",
        "Integration to day-to-day operation for actionable, real-time intelligence"
      ],
      benefits: [
        "Cost-effective project delivery",
        "Find insights through additional data layers",
        "Streamline your Operations through dataset diversifications",
        "Achieve deliverables without compromising data and cyber security"
      ]
    }
  ];

  const industryServices = [
    {
      icon: <Truck className="w-6 h-6 text-blue-600" />,
      title: "Freight, Brokerage and Duties/Tariffs Auditing",
      description: "Specialized auditing for shipping and logistics invoices, ensuring accurate freight charges and carrier compliance."
    },
    {
      icon: <Zap className="w-6 h-6 text-teal-600" />,
      title: "Utility Management",
      description: "Comprehensive utility bill analysis and management for manufacturing facilities and office locations."
    },
    {
      icon: <Package className="w-6 h-6 text-blue-600" />,
      title: "Supplier Invoice Processing",
      description: "End-to-end processing of supplier invoices with accuracy verification and approval workflows."
    },
    {
      icon: <DollarSign className="w-6 h-6 text-teal-600" />,
      title: "Cost Optimization",
      description: "Identify cost-saving opportunities through detailed analysis of spending patterns and vendor performance."
    }
  ];

  const processSteps = [
    {
      step: "1",
      title: "Assessment",
      description: "We analyze your current processes and identify improvement opportunities."
    },
    {
      step: "2", 
      title: "Implementation",
      description: "Our team implements customized solutions tailored to your business needs."
    },
    {
      step: "3",
      title: "Monitoring",
      description: "Continuous monitoring and optimization to ensure maximum efficiency."
    },
    {
      step: "4",
      title: "Reporting",
      description: "Regular reports and insights to keep you informed of performance and savings."
    }
  ];

  const activeServiceData = mainServices.find(service => service.id === activeService);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-teal-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Badge className="mb-4 bg-blue-100 text-blue-700 hover:bg-blue-200">
              Professional Services
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Comprehensive{' '}
              <span className="bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
                Data and Processing Solutions
              </span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-4">
              Unlock greater control over your business performance with any of our Core Services you can choose from.
            </p>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Leverage your existing data to uncover insights that will drive smarter, more valuable decisions.
            </p>
          </div>
        </div>
      </section>

      {/* Main Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Core Services
            </h2>
            <p className="text-xl text-gray-600">
              Explore our comprehensive range of services designed to optimize your business operations.
            </p>
          </div>

          <Tabs value={activeService} onValueChange={setActiveService} className="w-full">
            <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4 mb-8">
              {mainServices.map((service) => (
                <TabsTrigger key={service.id} value={service.id} className="text-sm">
                  {service.title}
                </TabsTrigger>
              ))}
            </TabsList>

            {mainServices.map((service) => (
              <TabsContent key={service.id} value={service.id} className="mt-8">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                  <div>
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-br from-blue-50 to-teal-50 rounded-2xl flex items-center justify-center mr-4">
                        {service.icon}
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">{service.title}</h3>
                        <p className="text-gray-600">{service.shortDesc}</p>
                      </div>
                    </div>
                    <p className="text-lg text-gray-700 mb-8">{service.fullDesc}</p>
                    
                    <div className="space-y-3 mb-8">
                      <h4 className="font-semibold text-gray-900">Key Features:</h4>
                      {service.features.map((feature, index) => (
                        <div key={index} className="flex items-center space-x-3">
                          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                          <span className="text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <Link to="/contact">
                      <Button className="bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700">
                        Get Started Today
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                  </div>

                  <div>
                    <Card className="border-0 shadow-xl p-8 bg-gradient-to-br from-gray-50 to-blue-50">
                      <CardHeader>
                        <CardTitle className="text-xl font-semibold text-gray-900 mb-4">
                          Benefits You'll Experience
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {service.benefits.map((benefit, index) => (
                          <div key={index} className="flex items-start space-x-3">
                            <TrendingUp className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">{benefit}</span>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Industry-Specific Services */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Industry-Specific Solutions
            </h2>
            <p className="text-xl text-gray-600">
              Tailored services for your specific industry requirements.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {industryServices.map((service, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-0 shadow-lg hover:-translate-y-2">
                <CardHeader className="text-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-50 to-teal-50 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                    {service.icon}
                  </div>
                  <CardTitle className="text-lg font-semibold text-gray-900">
                    {service.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 text-center">
                    {service.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Proven Process
            </h2>
            <p className="text-xl text-gray-600">
              A systematic approach to transforming your back-office operations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-white font-bold text-xl">{step.step}</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-blue-600 to-teal-600 py-20">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Optimize Your Operations?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Let us handle your back-office complexities while you focus on growing your business.
            Get a free consultation and see how we can help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-8">
                Get Free Consultation
              </Button>
            </Link>
            <Link to="/about">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-600 text-lg px-8">
                Learn About Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;